import { playerName } from "../types";
import type { GameView } from "../../../server/protocol";
import { Die } from "./Die";
export function HistoryPanel({ game: g }: { game: GameView }) {
  const p = g.public,
    n = (id: string | null) => playerName(p.players ?? [], id);
  const ballots = (items: any[]) => (
    <ul className="plain-list">
      {items.map((v, i) => (
        <li
          key={i}
        >{`${n(v.playerId)} → ${"approve" in v ? (v.approve ? "Approve" : "Reject") : v.targetId ? n(v.targetId) : "abstained"}`}</li>
      ))}
    </ul>
  );
  return (
    <div className="play-history">
      {g.id === "spectrum" &&
      p.clue &&
      !p.history?.some((v: any) => v.round === g.round) ? (
        <article>
          <h3>Round {g.round} · current clue</h3>
          <p>{p.clue}</p>
        </article>
      ) : null}
      {(p.clues ?? []).map((v: any, i: number) => (
        <p key={`c${i}`}>
          <small>{n(v.playerId)}</small>
          <strong>“{v.text}”</strong>
        </p>
      ))}
      {(p.exchanges ?? []).map((v: any, i: number) => (
        <article key={`x${i}`}>
          <h3>
            {n(v.questionerId)} → {n(v.answererId)}
          </h3>
          <p>{v.question.text ?? v.question.note ?? "Asked aloud"}</p>
          {v.answer ? (
            <p>{v.answer.text ?? v.answer.note ?? "Answered aloud"}</p>
          ) : null}
        </article>
      ))}
      {(p.accusationHistory ?? []).map((v: any, i: number) => (
        <article key={`a${i}`}>
          <h3>
            Vote recap · Round {v.round}
            {v.runoff ? " · runoff" : ""}
          </h3>
          {ballots(v.ballots)}
        </article>
      ))}
      {!p.accusationHistory?.length && p.revealedBallots?.length ? (
        <article>
          <h3>Vote recap</h3>
          {ballots(p.revealedBallots)}
        </article>
      ) : null}
      {(p.electionHistory ?? []).map((v: any, i: number) => (
        <article key={`e${i}`}>
          <h3>
            Election {(p.omittedElections ?? 0) + i + 1} ·{" "}
            {v.approved ? "approved" : "rejected"}
          </h3>
          {ballots(v.votes)}
        </article>
      ))}
      {p.omittedElections ? (
        <p>{p.omittedElections} earlier elections omitted.</p>
      ) : null}
      {(p.policyHistory ?? []).map((v: any, i: number) => (
        <p key={`p${i}`}>
          Round {v.round}: {v.policy}
          {v.chaos ? " · election tracker" : ""}
        </p>
      ))}
      {(p.publicActions ?? []).map((v: any, i: number) => (
        <p key={`u${i}`}>
          {v.type ?? v.power} {v.targetId ? n(v.targetId) : ""}
        </p>
      ))}
      {(p.nightHistory ?? []).map((v: any, i: number) => (
        <p key={`n${i}`}>
          Day {v.day} ·{" "}
          {v.eliminatedId
            ? `${n(v.eliminatedId)} did not survive the night.`
            : "Everyone survived the night."}
        </p>
      ))}
      {(p.voteHistory ?? []).map((v: any, i: number) => (
        <article key={`v${i}`}>
          <h3>
            Day {v.day} vote ·{" "}
            {v.eliminatedId
              ? `${n(v.eliminatedId)} eliminated`
              : "No elimination"}
          </h3>
          {ballots(v.ballots)}
        </article>
      ))}
      {p.historyOmitted?.nights || p.historyOmitted?.votes ? (
        <p>
          Earlier records omitted: {p.historyOmitted.nights} nights,{" "}
          {p.historyOmitted.votes} votes.
        </p>
      ) : null}
      {g.id === "liar-dice" && g.phase === "bidding" && p.bidHistory?.length ? (
        <article>
          <h3>This round’s bids</h3>
          {p.bidHistory.map((v: any, i: number) => (
            <p key={i}>
              {n(v.playerId)} bid {v.quantity} × {v.face}
            </p>
          ))}
          {p.omittedBids ? <p>{p.omittedBids} earlier bids omitted.</p> : null}
        </article>
      ) : null}
      {(p.roundHistory ?? []).map((v: any, i: number) => (
        <article key={`d${i}`}>
          <h3>
            Round {v.round ?? i + 1} · {v.actualCount} matched
          </h3>
          <p>
            {n(v.loserId)} lost a die{v.eliminated ? " and was eliminated" : ""}
            .
          </p>
          {(v.bids ?? []).map((b: any, j: number) => (
            <p key={j}>
              {n(b.playerId)} bid {b.quantity} × {b.face}
            </p>
          ))}
          {v.omittedBids ? <p>{v.omittedBids} earlier bids omitted.</p> : null}
          {v.earlierBidsUnavailable ? (
            <p>Earlier bids unavailable for this saved round.</p>
          ) : null}
          {v.rolls?.map((r: any) => (
            <div className="play-history-roll" key={r.playerId}>
              <span>{n(r.playerId)}</span>
              <div className="dice-row">
                {r.dice.map((face: number, j: number) => (
                  <Die
                    key={j}
                    value={face}
                    match={face === v.bid?.face || (p.onesWild && face === 1)}
                  />
                ))}
              </div>
            </div>
          ))}
        </article>
      ))}
      {(p.history ?? []).map((v: any, i: number) => (
        <article key={`s${i}`}>
          <h3>
            Round {v.round} · +{v.points} points
          </h3>
          <p>
            {v.left} ↔ {v.right} · “{v.clue}”
          </p>
          <p>
            Target {v.target}, team guess {v.groupGuess} · {v.error} away
          </p>
          {v.guesses?.map((x: any) => (
            <p key={x.playerId}>
              {n(x.playerId)} → {x.value}
            </p>
          ))}
        </article>
      ))}
      {![
        "clues",
        "exchanges",
        "accusationHistory",
        "revealedBallots",
        "electionHistory",
        "policyHistory",
        "nightHistory",
        "voteHistory",
        "roundHistory",
        "bidHistory",
        "history",
      ].some((k) => p[k]?.length) ? (
        <p className="muted">No history yet.</p>
      ) : null}
    </div>
  );
}
